/**
 * German legal texts (roadmap LEG-1), adapted from PrestoCRM's to what PrestoBooks does: receipts,
 * card and bank statements, emailed receipts, read by Mistral. To be reviewed once by an Austrian
 * lawyer before launch. Operator details and sub-processors come from lib/legal/operator.ts.
 */
import { OPERATOR as O, SUBPROCESSORS, TERMS_VERSION } from "@/lib/legal/operator";

const H2 = "pt-2 text-lg font-semibold";

function Mail() {
  return (
    <a className="underline" href={`mailto:${O.email}`}>
      {O.email}
    </a>
  );
}

function Subprocessors() {
  return (
    <ul className="list-disc space-y-1 pl-5">
      {SUBPROCESSORS.map((item) => (
        <li key={item.name}>
          <span className="font-semibold">{item.name}</span>: {item.purpose}. Standort:{" "}
          {item.location}
        </li>
      ))}
    </ul>
  );
}

export function ImprintText() {
  return (
    <>
      <p>Informationen und Offenlegung nach § 5 ECG, § 25 MedienG, § 63 GewO und § 14 UGB.</p>

      <h2 className={H2}>Diensteanbieter</h2>
      <p>
        {O.name} ({O.legalForm})
        <br />
        {O.careOf}
        <br />
        {O.street}
        <br />
        {O.city}, {O.country}
      </p>
      {O.representative && <p>Geschäftsführer: {O.representative}</p>}

      <h2 className={H2}>Kontakt</h2>
      <p>
        E-Mail: <Mail />
        <br />
        Telefon: {O.phone}
      </p>

      <h2 className={H2}>Firmenbuch</h2>
      <p>
        Firmenbuchnummer: {O.register}
        <br />
        Firmenbuchgericht: {O.court}
        {O.vatId && (
          <>
            <br />
            UID-Nummer: {O.vatId}
          </>
        )}
      </p>

      <h2 className={H2}>Gewerbe und Kammer</h2>
      <p>
        Unternehmensgegenstand: {O.business}
        <br />
        Mitgliedschaft: {O.chamber}
        <br />
        Anwendbare Rechtsvorschriften: Gewerbeordnung, abrufbar unter{" "}
        <a className="underline" href="https://www.ris.bka.gv.at" rel="noreferrer" target="_blank">
          www.ris.bka.gv.at
        </a>
      </p>

      <h2 className={H2}>Streitbeilegung</h2>
      <p>
        PrestoBooks richtet sich ausschließlich an Unternehmer. Die Bestimmungen über die
        alternative Streitbeilegung in Verbraucherangelegenheiten sind daher nicht anwendbar; wir
        nehmen an Verfahren vor einer Verbraucherschlichtungsstelle nicht teil.
      </p>

      <h2 className={H2}>Offenlegung nach § 25 Mediengesetz</h2>
      <p>
        Medieninhaber: {O.name}, {O.careOf}, {O.street}, {O.city}.
      </p>
      <p>
        Blattlinie: Information über die Software PrestoBooks, ihre Funktionen, Tarife und
        Neuerungen sowie über Buchhaltung und Steuern für Unternehmen in Österreich.
      </p>

      <h2 className={H2}>Urheberrecht</h2>
      <p>
        Die Inhalte dieser Website und der App unterliegen, soweit rechtlich möglich, Schutzrechten
        wie dem Urheberrecht. Eine Verwendung oder Verbreitung bedarf der schriftlichen Zustimmung
        des Betreibers. Die Urheberrechte Dritter beachten wir mit größter Sorgfalt; sollten Sie auf
        eine Verletzung aufmerksam werden, bitten wir um einen Hinweis, und wir entfernen den Inhalt
        umgehend.
      </p>

      <h2 className={H2}>Haftung für Links</h2>
      <p>
        Inhalte und ausgehende Links haben wir mit größtmöglicher Sorgfalt geprüft. Für Inhalte
        externer Websites sind ausschließlich deren Betreiber verantwortlich. Weisen Sie uns auf
        einen Link zu rechtswidrigen Inhalten hin, entfernen wir ihn umgehend nach § 17 Abs. 2 ECG.
      </p>
    </>
  );
}

export function PrivacyText() {
  return (
    <>
      <h2 className={H2}>1. Verantwortlicher</h2>
      <p>
        Verantwortlich für die Verarbeitung personenbezogener Daten auf der Website prestobooks.at
        und in der App PrestoBooks ist {O.name}, {O.careOf}, {O.street}, {O.city}, {O.country}.
        E-Mail: <Mail />
      </p>

      <h2 className={H2}>2. Überblick</h2>
      <p>
        PrestoBooks ist eine Software für die Belegverwaltung und Buchhaltungsvorbereitung von
        Unternehmen in Österreich. Wir verarbeiten personenbezogene Daten, um Ihnen die Website und
        die App bereitzustellen, Ihr Konto und Ihre Betriebe zu führen, Belege zu lesen, Fragen im
        Assistenten zu beantworten, Abonnements abzurechnen, die Nutzung ohne Cookies zu messen und
        Fehler zu beheben. App, Datenbank und Belege liegen in einem Rechenzentrum in Falkenstein
        (Deutschland).
      </p>

      <h2 className={H2}>3. Server-Protokolle</h2>
      <p>
        Beim Aufruf der Website und der App werden technisch notwendige Daten (IP-Adresse,
        Zeitpunkt, aufgerufene Adresse, Browser und Betriebssystem) in Server-Protokollen
        verarbeitet, um die Inhalte auszuliefern, Fehler zu finden und Missbrauch abzuwehren.
        Rechtsgrundlage ist unser berechtigtes Interesse an einem sicheren und stabilen Betrieb
        (Art. 6 Abs. 1 lit. f DSGVO). Die Protokolle werden nach spätestens 30 Tagen gelöscht.
      </p>

      <h2 className={H2}>4. Konto</h2>
      <p>
        Für Ihr Konto speichern wir Ihren Namen, Ihre E-Mail-Adresse, ein nur als Hash gespeichertes
        Passwort, Ihre Sprache, die akzeptierte Fassung der AGB mit Zeitpunkt sowie Ihre
        Mitgliedschaften und Rollen in Betrieben. Rechtsgrundlage ist die Erfüllung des Vertrags
        (Art. 6 Abs. 1 lit. b DSGVO). Einladungen in einen Betrieb, etwa an die Steuerberatung,
        versenden wir an die vom einladenden Mitglied angegebene E-Mail-Adresse.
      </p>

      <h2 className={H2}>5. Inhalte Ihres Betriebs</h2>
      <p>
        Belege und Rechnungen (Fotos, PDFs, E-Rechnungen und die daraus gelesenen Daten),
        Kreditkarten- und Kontoumsätze, per E-Mail eingesandte Belege samt der E-Mail, Notizen (etwa
        Anlass und Gäste einer Bewirtung), Kategorien, Personenkonten und die Stammdaten des
        Betriebs speichern wir, damit Sie mit ihnen arbeiten können. Für personenbezogene Daten
        darin (z. B. Namen von Lieferanten, Kunden oder Gästen) sind Sie als Kunde Verantwortlicher;
        wir verarbeiten sie als Auftragsverarbeiter ausschließlich in Ihrem Auftrag nach dem{" "}
        <a className="underline" href="/avv">
          Auftragsverarbeitungsvertrag (AVV)
        </a>
        . Diese Datenschutzerklärung betrifft insoweit nur unsere eigene Verarbeitung.
      </p>

      <h2 className={H2}>6. Lokaler Speicher, Reichweitenmessung und Fehlerberichte</h2>
      <p>
        Die App speichert in Ihrem Browser Ihre Anmeldung, den zuletzt genutzten Betrieb, Ihre
        Sprache, die Darstellung (hell oder dunkel) und noch nicht gesendete Belege der Handy-App.
        Diese Einträge sind für den von Ihnen gewünschten Dienst unbedingt erforderlich (§ 165 Abs.
        3 Telekommunikationsgesetz 2021) und benötigen keine Einwilligung. Cookies setzt die App
        nicht.
      </p>
      <h3 className="font-semibold">Reichweitenmessung mit Plausible</h3>
      <p>
        Um zu verstehen, welche Seiten und Funktionen der App genutzt werden, verwenden wir
        Plausible Analytics (Plausible Insights OÜ, Estland; Server in Deutschland). Plausible setzt
        keine Cookies, speichert nichts in Ihrem Browser und speichert keine IP-Adressen: Besuche
        werden nur über eine täglich wechselnde Prüfsumme aus IP-Adresse und Browserkennung gezählt,
        die sich nicht auf Sie zurückführen lässt. Erfasst werden die aufgerufene Seite (ohne
        Kennungen einzelner Belege), die verweisende Website, Land, Gerätetyp, Browser und
        Betriebssystem sowie einzelne Aktionen wie „Beleg gebucht“, ohne Inhalte Ihrer Belege, ohne
        Beträge und ohne Bezug zu Ihrer Person. Die Messung läuft über unseren eigenen Server; Ihr
        Browser verbindet sich dabei nicht mit Plausible. Rechtsgrundlage ist unser berechtigtes
        Interesse an einer datensparsamen Reichweitenmessung (Art. 6 Abs. 1 lit. f DSGVO).
      </p>
      <h3 className="font-semibold">Fehlerberichte mit Sentry</h3>
      <p>
        Tritt in der App oder in einem unserer Dienste ein technischer Fehler auf, senden wir einen
        Fehlerbericht an Sentry (Functional Software, Inc., USA; Speicherung in der EU-Datenregion
        Frankfurt am Main). Er enthält die Fehlermeldung, den technischen Ablauf (Stacktrace), die
        betroffene Seite ohne Parameter sowie Browser- und Betriebssystemversion. Angaben zu Ihrer
        Person, IP-Adresse, Cookies, Formular- und Anfrageinhalte sowie Inhalte Ihrer Belege senden
        wir nicht mit. Wir nutzen die Berichte, um Fehler zu finden und zu beheben (Art. 6 Abs. 1
        lit. f DSGVO); sie werden nach höchstens 90 Tagen gelöscht.
      </p>

      <h2 className={H2}>7. Auslesen der Belege und Assistent über Mistral AI</h2>
      <p>
        Damit Lieferant, Datum, Beträge und Steuersätze nicht abgetippt werden müssen, senden wir
        hochgeladene, fotografierte und per E-Mail eingesandte Belege zur Texterkennung und zum
        Auslesen an Mistral AI SAS (Frankreich). Rechtsgrundlage ist die Vertragserfüllung (Art. 6
        Abs. 1 lit. b DSGVO); für personenbezogene Daten in den Belegen gilt der AVV. Ausgelesene
        Werte sind Vorschläge, die eine Person prüft, bevor ein Beleg verbucht wird.
      </p>
      <p>
        Im Assistenten können Sie Fragen zur Buchhaltung und zur App stellen, getippt oder als
        Sprachnachricht. Zur Beantwortung übermitteln wir Ihre Nachricht mit dem bisherigen Verlauf
        der Unterhaltung an Mistral AI SAS; eine Sprachnachricht wird dort in Text umgewandelt, die
        Aufnahme selbst speichern wir nicht. Der Assistent hat keinen Zugriff auf Ihre Belege,
        Umsätze oder anderen Daten Ihres Betriebs. Ihre Unterhaltungen speichern wir in Ihrem Konto,
        nur für Sie sichtbar, bis Sie sie löschen oder Ihr Konto löschen. Rechtsgrundlage ist die
        Vertragserfüllung (Art. 6 Abs. 1 lit. b DSGVO). Geben Sie im Assistenten bitte keine Daten
        anderer Personen ein, die Sie für Ihre Frage nicht brauchen.
      </p>

      <h2 className={H2}>8. E-Mails über Lettermint</h2>
      <p>
        Bestätigungen, Passwort-E-Mails, Einladungen und Mitteilungen zu Ihrem Konto versenden wir
        über Lettermint (Lettermint B.V., Niederlande; Server ausschließlich in Europa), ohne
        Öffnungs- oder Klickverfolgung. Belege, die Sie an die Beleg-Adresse Ihres Betriebs senden,
        nimmt ebenfalls Lettermint entgegen und übergibt sie unserem Server; Lettermint bewahrt
        eingegangene Nachrichten bis zu 28 Tage auf. Dabei werden E-Mail-Adressen, Namen und Inhalt
        der Nachrichten samt Anhängen verarbeitet (Art. 6 Abs. 1 lit. b DSGVO).
      </p>

      <h2 className={H2}>9. Zahlungen über Stripe</h2>
      <p>
        Kostenpflichtige Tarife werden über Stripe Payments Europe, Ltd. (Irland) abgewickelt.
        Zahlungsdaten (Karte, SEPA-Lastschrift) gibt der Inhaber des Betriebs direkt bei Stripe ein;
        wir erhalten nur Tarif, Status, Laufzeit und Rechnungsdaten des Abonnements. Rechtsgrundlage
        sind die Vertragserfüllung und unsere gesetzlichen Aufzeichnungspflichten (Art. 6 Abs. 1
        lit. b und c DSGVO).
      </p>

      <h2 className={H2}>10. Hosting</h2>
      <p>
        App, Datenbank, Belege und Dienste betreibt die Hetzner Online GmbH (Gunzenhausen) für uns
        in einem Rechenzentrum in Falkenstein (Deutschland). Die gespeicherten Daten bleiben in der
        EU; Sicherungskopien ebenso. Die Website prestobooks.at liefert Cloudflare, Inc. aus; dabei
        werden nur die technisch nötigen Verbindungsdaten Ihres Browsers verarbeitet, keine Daten
        aus der App.
      </p>

      <h2 className={H2}>11. Empfänger und Auftragsverarbeiter</h2>
      <p>
        Wir setzen folgende Dienstleister als Auftragsverarbeiter ein, jeweils mit einem Vertrag
        nach Art. 28 DSGVO:
      </p>
      <Subprocessors />
      <p>
        Personen, die Sie in Ihren Betrieb einladen (etwa Ihre Steuerberatung), sehen dessen Daten
        im Umfang der Rolle, die Sie ihnen geben.
      </p>

      <h2 className={H2}>12. Übermittlung in Drittländer</h2>
      <p>
        Cloudflare, Inc. (nur Website) und Konzerngesellschaften von Stripe haben ihren Sitz in den
        USA. Ein Zugriff aus den USA, etwa für Support oder Betrieb, ist daher nicht völlig
        ausgeschlossen. Solche Übermittlungen stützen sich auf die Zertifizierung nach dem EU-US
        Data Privacy Framework (Angemessenheitsbeschluss, Art. 45 DSGVO), soweit vorhanden, und auf
        EU-Standardvertragsklauseln (Art. 46 Abs. 2 lit. c DSGVO).
      </p>

      <h2 className={H2}>13. Speicherdauer</h2>
      <p>
        Wir speichern Konto- und Betriebsdaten, solange das Konto beziehungsweise der Betrieb
        besteht. Nach Vertragsende bleiben die Daten schreibgeschützt abrufbar und exportierbar, wie
        es die AGB regeln, und werden danach gelöscht, soweit keine gesetzliche Aufbewahrungspflicht
        besteht. Unsere eigenen Buchhaltungsunterlagen (z. B. Rechnungen an Sie) bewahren wir nach §
        132 BAO und § 212 UGB sieben Jahre auf. Sicherungskopien werden spätestens 30 Tage nach der
        Löschung überschrieben.
      </p>

      <h2 className={H2}>14. Ihre Rechte</h2>
      <p>
        Sie haben das Recht auf Auskunft, Berichtigung, Löschung, Einschränkung der Verarbeitung,
        Datenübertragbarkeit und Widerspruch (Art. 15 bis 21 DSGVO) sowie das Recht, eine erteilte
        Einwilligung jederzeit mit Wirkung für die Zukunft zu widerrufen. Alle Daten eines Betriebs
        können Sie in der App unter Einstellungen → Daten exportieren, Ihr Konto löschen Sie unter
        Profil. Betrifft Ihre Anfrage Daten, die ein Kunde von PrestoBooks in seinem Betrieb über
        Sie gespeichert hat, wenden Sie sich bitte an diesen Kunden als Verantwortlichen; wir leiten
        an uns gerichtete Anfragen weiter.
      </p>
      <p>
        Sie können sich bei der österreichischen Datenschutzbehörde beschweren: Datenschutzbehörde,
        Barichgasse 40–42, 1030 Wien,{" "}
        <a className="underline" href="https://www.dsb.gv.at" rel="noreferrer" target="_blank">
          www.dsb.gv.at
        </a>
        .
      </p>

      <h2 className={H2}>15. Sicherheit</h2>
      <p>
        Verbindungen sind TLS-verschlüsselt, Passwörter werden nur als Hash gespeichert, Belege sind
        nur mit einem kurzlebigen Zugriffsschlüssel abrufbar, und jeder Betrieb ist serverseitig von
        allen anderen getrennt. Einzelheiten stehen in Anlage 1 des AVV.
      </p>

      <h2 className={H2}>16. Kontakt und Änderungen</h2>
      <p>
        Fragen zum Datenschutz richten Sie bitte an <Mail />. Wir passen diese Erklärung an, wenn
        sich der Dienst oder die Rechtslage ändert; es gilt die hier veröffentlichte Fassung.
      </p>
    </>
  );
}

export function TermsText() {
  return (
    <>
      <p>Fassung {TERMS_VERSION}</p>

      <h2 className={H2}>§ 1 Geltungsbereich</h2>
      <p>
        Diese Allgemeinen Geschäftsbedingungen (AGB) gelten für alle Verträge über die Nutzung von
        PrestoBooks (Website und App, „Dienst“), angeboten von {O.name} („wir“, „Anbieter“). Der
        Dienst richtet sich ausschließlich an Unternehmer im Sinne des § 1 UGB; Verträge mit
        Verbrauchern schließen wir nicht, das Konsumentenschutzgesetz (KSchG) ist nicht anwendbar.
        Abweichende oder ergänzende Bedingungen des Kunden gelten nicht, auch wenn wir ihnen nicht
        ausdrücklich widersprechen.
      </p>

      <h2 className={H2}>§ 2 Leistungen</h2>
      <p>
        PrestoBooks ist eine webbasierte Software (Software as a Service) zum Erfassen, Auslesen,
        Prüfen und Ablegen von Belegen, zum Abgleich mit Kreditkarten- und Kontoumsätzen, für den
        Periodenabschluss, den Export für die Steuerberatung (z. B. für BMD, RZL oder DATEV) sowie
        für Entwürfe der Umsatzsteuervoranmeldung und der Einnahmen-Ausgaben-Rechnung. Der
        Leistungsumfang ergibt sich aus dem gewählten Tarif (Start, Standard, Plus) laut der
        Tarifbeschreibung zum Zeitpunkt des Vertragsschlusses. Wir dürfen den Dienst
        weiterentwickeln, sofern wesentliche Funktionen des gebuchten Tarifs erhalten bleiben.
      </p>
      <p>
        PrestoBooks ist ein Werkzeug und keine Steuerberatung. Wir erbringen keine Leistungen, die
        Steuerberatern oder Bilanzbuchhaltern vorbehalten sind (Wirtschaftstreuhandberufsgesetz,
        Bilanzbuchhaltungsgesetz), reichen keine Erklärungen bei Behörden ein und vertreten den
        Kunden nicht. Das gilt auch für die Antworten des Assistenten: Sie werden automatisch
        erzeugt, können fehlerhaft sein und ersetzen keine Beratung im Einzelfall.
      </p>

      <h2 className={H2}>§ 3 Konto, Betriebe und Mitglieder</h2>
      <ul className="list-disc space-y-1 pl-5">
        <li>
          Die Nutzung setzt ein Konto mit gültiger E-Mail-Adresse voraus. Mit der Registrierung
          bestätigen Sie, als Unternehmer oder für ein Unternehmen zu handeln, und dass die Angaben
          richtig sind.
        </li>
        <li>
          Daten werden je Betrieb geführt. Vertragspartner ist das Unternehmen, für das der Betrieb
          angelegt wird; der Inhaber des Betriebs handelt für dieses Unternehmen. Ein Konto kann
          mehrere Betriebe führen, jeder mit eigenem Tarif.
        </li>
        <li>
          Der Kunde kann Mitglieder einladen und ihnen Rollen zuweisen (Inhaber, Mitglied,
          Steuerberatung, Nur Lesen). Er ist für die Vergabe der Rollen und für alle Handlungen der
          Mitglieder in seinem Betrieb verantwortlich. Der Zugang der Steuerberatung ist kostenlos.
        </li>
        <li>
          Zugangsdaten sind geheim zu halten. Besteht der Verdacht eines Missbrauchs, informiert uns
          der Kunde unverzüglich.
        </li>
      </ul>

      <h2 className={H2}>§ 4 Testphase</h2>
      <p>
        Jeder neue Betrieb kann PrestoBooks 14 Tage lang kostenlos mit dem Funktionsumfang des
        Tarifs Standard testen. Dafür sind keine Zahlungsdaten nötig, und die Testphase endet
        automatisch, ohne dass ein kostenpflichtiger Vertrag entsteht. Wird bis zum Ende der
        Testphase kein Tarif gebucht, wird der Betrieb schreibgeschützt: Die Daten bleiben sichtbar
        und exportierbar, können aber nicht mehr geändert oder ergänzt werden, bis ein Tarif gebucht
        wird.
      </p>

      <h2 className={H2}>§ 5 Tarife, Preise und Zahlung</h2>
      <ul className="list-disc space-y-1 pl-5">
        <li>
          Es gelten die bei der Buchung angezeigten Preise. Alle Preise verstehen sich netto
          zuzüglich der gesetzlichen Umsatzsteuer; bei Kunden mit gültiger UID-Nummer in einem
          anderen EU-Mitgliedstaat gilt das Reverse-Charge-Verfahren.
        </li>
        <li>
          Die Entgelte werden je nach Wahl jährlich oder monatlich im Voraus über unseren
          Zahlungsdienstleister Stripe abgerechnet, per Kreditkarte oder SEPA-Lastschrift. Die
          Rechnung erhält der Kunde elektronisch.
        </li>
        <li>
          Das Abonnement verlängert sich jeweils um die gewählte Abrechnungsperiode (Monat oder
          Jahr), wenn es nicht nach § 6 gekündigt wird. Der bei der Buchung geltende Preis bleibt,
          solange das Abonnement läuft, außer bei einer Preisänderung nach dem folgenden Punkt.
        </li>
        <li>
          Preisänderungen kündigen wir mindestens 30 Tage vor ihrem Wirksamwerden per E-Mail an; sie
          gelten ab der nächsten Abrechnungsperiode. Der Kunde kann das Abonnement bis zum
          Wirksamwerden der Änderung zu diesem Zeitpunkt kündigen; darauf weisen wir in der
          Mitteilung hin.
        </li>
        <li>
          Der Inhaber kann den Tarif und die Abrechnungsperiode jederzeit wechseln. Ein Wechsel auf
          einen teureren Tarif oder von monatlicher auf jährliche Abrechnung gilt sofort; für den
          Rest der laufenden Periode wird der Unterschied anteilig sofort verrechnet. Ein Wechsel
          auf einen günstigeren Tarif oder auf monatliche Abrechnung gilt ab der nächsten
          Abrechnungsperiode.
        </li>
        <li>
          Bereits bezahlte Entgelte werden bei einer Kündigung oder einem Wechsel auf einen
          günstigeren Tarif nicht anteilig erstattet.
        </li>
        <li>
          Schlägt eine Zahlung fehl, informieren wir den Inhaber per E-Mail; der Tarif läuft sieben
          Tage ab dem ersten Fehlschlag weiter. Danach wird der Betrieb schreibgeschützt, bis die
          Zahlung eingegangen ist; der Export der Daten bleibt möglich.
        </li>
      </ul>

      <h2 className={H2}>§ 6 Laufzeit und Kündigung</h2>
      <ul className="list-disc space-y-1 pl-5">
        <li>
          Monatliche Abonnements kann der Kunde jederzeit zum Ende des laufenden Abrechnungsmonats
          kündigen, jährliche Abonnements zum Ende des laufenden Abrechnungsjahres.
        </li>
        <li>
          Die Kündigung erfolgt in der App unter Einstellungen → Tarif durch den Inhaber des
          Betriebs oder per E-Mail an uns. Wir können ordentlich mit einer Frist von drei Monaten
          zum Ende einer Abrechnungsperiode kündigen.
        </li>
        <li>
          Nach Wirksamwerden der Kündigung wird der Betrieb schreibgeschützt; die Daten bleiben nach
          § 11 abrufbar und exportierbar.
        </li>
        <li>Das Recht beider Seiten zur Kündigung aus wichtigem Grund bleibt unberührt.</li>
      </ul>

      <h2 className={H2}>§ 7 Pflichten des Kunden, Prüfung und Aufbewahrung</h2>
      <ul className="list-disc space-y-1 pl-5">
        <li>
          Der Kunde ist für die Inhalte in seinem Betrieb verantwortlich, insbesondere für die
          Rechtmäßigkeit der Verarbeitung personenbezogener Daten in seinen Belegen.
        </li>
        <li>
          Ausgelesene Werte, Prüfungen nach § 11 UStG, Zuordnungen von Umsätzen, Exporte und die
          Entwürfe der Umsatzsteuervoranmeldung und der Einnahmen-Ausgaben-Rechnung werden
          automatisch erzeugt und sind eine Hilfe ohne Gewähr. Der Kunde prüft sie, bevor er Belege
          verbucht, Exporte weitergibt oder Werte in Erklärungen übernimmt. Für die Richtigkeit
          seiner Buchhaltung und seiner Steuererklärungen bleibt der Kunde verantwortlich.
        </li>
        <li>
          Verbuchte Belege erhalten eine fortlaufende Belegnummer und können nicht mehr gelöscht,
          nur archiviert werden. Wir bewahren alle Belege auf, solange der Betrieb besteht. Die
          gesetzliche Aufbewahrung seiner Unterlagen (§ 132 BAO, sieben Jahre) obliegt weiterhin dem
          Kunden; er kann sie jederzeit exportieren und tut das, bevor er einen Betrieb löscht oder
          den Vertrag beendet.
        </li>
        <li>
          Der Kunde nutzt den Dienst nur rechtmäßig, lädt keine Inhalte hoch, die Rechte Dritter
          verletzen, und greift nicht in den Betrieb des Dienstes ein (z. B. durch automatisierte
          Massenabfragen oder Umgehen von Zugriffsbeschränkungen).
        </li>
      </ul>

      <h2 className={H2}>§ 8 Nutzungsrecht</h2>
      <p>
        Der Kunde erhält für die Vertragsdauer das nicht ausschließliche, nicht übertragbare Recht,
        den Dienst im Rahmen des gebuchten Tarifs für eigene geschäftliche Zwecke zu nutzen. Die
        Rechte an der Software verbleiben bei uns. Die Daten des Kunden gehören dem Kunden.
      </p>

      <h2 className={H2}>§ 9 Verfügbarkeit</h2>
      <p>
        Wir bemühen uns um eine hohe Verfügbarkeit, schulden aber kein bestimmtes Service-Level.
        Geplante Wartungsarbeiten kündigen wir nach Möglichkeit vorab an und führen sie außerhalb
        üblicher Geschäftszeiten durch. Ausfälle durch höhere Gewalt, Störungen des Internets oder
        Ursachen außerhalb unseres Einflussbereichs vertreten wir nicht.
      </p>

      <h2 className={H2}>§ 10 Datenschutz</h2>
      <p>
        Soweit wir personenbezogene Daten im Auftrag des Kunden verarbeiten, gilt der{" "}
        <a className="underline" href="/avv">
          Auftragsverarbeitungsvertrag (AVV)
        </a>
        , den der Kunde mit diesen AGB bei der Registrierung annimmt. Für unsere eigene Verarbeitung
        gilt die{" "}
        <a className="underline" href="/datenschutz">
          Datenschutzerklärung
        </a>
        .
      </p>

      <h2 className={H2}>§ 11 Anbieterwechsel und Datenexport (Data Act)</h2>
      <p>
        Wir gewährleisten den Wechsel nach Art. 23 bis 31 der Verordnung (EU) 2023/2854 (Data Act):
      </p>
      <ol className="list-decimal space-y-1 pl-5">
        <li>
          Der Kunde kann jederzeit zu einem anderen Anbieter oder auf eigene Infrastruktur wechseln.
          Er kann den Vertrag dafür mit einer Frist von höchstens zwei Monaten kündigen; kürzere
          Fristen nach § 6 bleiben unberührt.
        </li>
        <li>
          Exportierbar sind alle Daten des Betriebs: Belege mit allen Dateien, wie sie hochgeladen
          wurden, die ausgelesenen und erfassten Daten, Umsätze und Importe, Kategorien,
          Personenkonten, das Journal und die Stammdaten. Der Export erfolgt in strukturierten,
          gängigen und maschinenlesbaren Formaten (JSON, CSV, PDF und die Originaldateien),
          jederzeit selbst in der App unter Einstellungen → Daten. Nicht umfasst sind unsere
          Software, interne Betriebsdaten und Daten, die Rechte Dritter oder Geschäftsgeheimnisse
          des Anbieters berühren.
        </li>
        <li>
          Nach Vertragsende bleiben alle Daten mindestens 30 Tage schreibgeschützt abrufbar und
          exportierbar (Übergangs- und Abrufzeitraum). Ist ein Wechsel innerhalb dieser Frist
          technisch nicht möglich, teilen wir das binnen 14 Tagen nach dem Wechselverlangen mit und
          verlängern die Frist angemessen, höchstens auf sieben Monate. Der Kunde kann die Frist
          einmalig verlängern, wenn er das für den Wechsel braucht.
        </li>
        <li>
          Während des Wechsels unterstützen wir den Kunden und den neuen Anbieter in angemessenem
          Umfang, insbesondere mit Auskünften über Exportformate und Datenstrukturen, und
          gewährleisten die Fortführung des Dienstes bis zum Abschluss des Wechsels.
        </li>
        <li>
          Für den Wechsel und den Datenexport (Datenübertragung, Egress) erheben wir keine Entgelte.
        </li>
        <li>
          Nach Ablauf des Abrufzeitraums löschen wir die Daten des Betriebs, soweit keine
          gesetzliche Aufbewahrungspflicht besteht, und kündigen das dem Inhaber mindestens 30 Tage
          vorher per E-Mail an. Sicherungskopien werden innerhalb von 30 Tagen danach überschrieben.
        </li>
      </ol>

      <h2 className={H2}>§ 12 Haftung</h2>
      <ul className="list-disc space-y-1 pl-5">
        <li>Für Vorsatz und grobe Fahrlässigkeit haften wir unbeschränkt.</li>
        <li>
          Die Haftung für leichte Fahrlässigkeit ist ausgeschlossen, ausgenommen für Schäden aus der
          Verletzung des Lebens, des Körpers oder der Gesundheit.
        </li>
        <li>
          Soweit wir sonst haften und keine zwingende gesetzliche Haftung besteht, ist die Haftung
          je Vertragsjahr mit den in den letzten zwölf Monaten vor dem Schadensereignis vom Kunden
          gezahlten Entgelten begrenzt. Die Haftung für entgangenen Gewinn, Folgeschäden, Schäden
          Dritter sowie für Steuern, Zinsen, Zuschläge und Strafen, die auf ungeprüft übernommenen
          Werten beruhen, ist, soweit gesetzlich zulässig, ausgeschlossen.
        </li>
        <li>
          Der Kunde hat Schäden binnen sechs Monaten ab Kenntnis von Schaden und Schädiger
          gerichtlich geltend zu machen. Die Haftung nach dem Produkthaftungsgesetz bleibt
          unberührt.
        </li>
      </ul>

      <h2 className={H2}>§ 13 Änderungen dieser AGB</h2>
      <p>
        Änderungen dieser AGB teilen wir dem Inhaber des Betriebs mindestens 30 Tage vor ihrem
        Inkrafttreten per E-Mail mit. Widerspricht der Kunde nicht bis zum Inkrafttreten, gelten sie
        als angenommen; auf diese Wirkung und das Widerspruchsrecht weisen wir in der Mitteilung
        gesondert hin. Widerspricht der Kunde, können beide Seiten den Vertrag zum Inkrafttreten der
        Änderung kündigen; bis dahin gelten die bisherigen AGB.
      </p>

      <h2 className={H2}>§ 14 Schlussbestimmungen</h2>
      <ul className="list-disc space-y-1 pl-5">
        <li>
          Es gilt österreichisches Recht unter Ausschluss seiner Verweisungsnormen und des
          UN-Kaufrechts (CISG).
        </li>
        <li>
          Ausschließlicher Gerichtsstand ist das für den Sitz des Anbieters sachlich zuständige
          Gericht. Wir dürfen den Kunden auch an seinem allgemeinen Gerichtsstand klagen.
        </li>
        <li>
          Ist eine Bestimmung unwirksam, bleiben die übrigen wirksam. An die Stelle der unwirksamen
          tritt eine wirksame Regelung, die ihrem wirtschaftlichen Zweck am nächsten kommt.
        </li>
        <li>
          Änderungen und Ergänzungen des Vertrags bedürfen der Schriftform; E-Mail genügt. Das gilt
          auch für ein Abgehen von diesem Formerfordernis. Erklärungen in der App (z. B.
          Registrierung, Buchung, Kündigung) wahren die Form.
        </li>
      </ul>
    </>
  );
}

export function DpaText() {
  return (
    <>
      <p>
        Vertrag zur Auftragsverarbeitung nach Art. 28 DSGVO zwischen dem Kunden als Verantwortlichem
        („Kunde“) und {O.name}, {O.street}, {O.city}, als Auftragsverarbeiter („Anbieter“), zu den{" "}
        <a className="underline" href="/agb">
          AGB
        </a>{" "}
        (Fassung {TERMS_VERSION}).
      </p>

      <h2 className={H2}>1. Abschluss</h2>
      <p>
        Der Vertrag wird online geschlossen: Bei der Registrierung nimmt die Person, die das Konto
        anlegt, ihn zusammen mit den AGB für die Unternehmen an, für die sie Betriebe anlegt. Der
        Anbieter speichert dabei, wer welche Fassung wann angenommen hat. Eine Unterschrift auf
        Papier ist für die Wirksamkeit nicht erforderlich (Art. 28 Abs. 9 DSGVO, elektronisches
        Format).
      </p>

      <h2 className={H2}>2. Gegenstand und Dauer</h2>
      <p>
        Der Anbieter verarbeitet personenbezogene Daten, die der Kunde und seine Mitglieder in
        PrestoBooks speichern oder per E-Mail einsenden, ausschließlich, um den Dienst nach den AGB
        zu erbringen. Der Vertrag gilt für die Dauer des Nutzungsvertrags und darüber hinaus,
        solange der Anbieter Daten des Kunden verarbeitet (einschließlich des Abrufzeitraums nach
        Vertragsende).
      </p>

      <h2 className={H2}>3. Art und Zweck der Verarbeitung</h2>
      <p>
        Bereitstellung einer Software für Belegverwaltung und Buchhaltungsvorbereitung als Software
        as a Service: Empfangen, Speichern, Zuschneiden und Auslesen von Belegen (auch per E-Mail),
        Importieren von Kreditkarten- und Kontoumsätzen und deren Abgleich mit Belegen, Ordnen,
        Durchsuchen, Anzeigen und Exportieren der Daten, Erstellen von Exportdateien und Berichten,
        Versand von Systemnachrichten, Sicherung und Löschung.
      </p>

      <h2 className={H2}>4. Art der Daten und Kategorien Betroffener</h2>
      <ul className="list-disc space-y-1 pl-5">
        <li>
          Betroffene: Geschäftspartner des Kunden (Lieferanten, Kunden und deren Ansprechpartner),
          in Belegen genannte Personen (z. B. Gäste einer Bewirtung), Karteninhaber sowie
          Beschäftigte und sonstige Mitglieder des Kunden, die den Betrieb nutzen, einschließlich
          der Steuerberatung.
        </li>
        <li>
          Daten: Namen, Anschriften, UID-Nummern, Kontaktdaten, Rechnungs- und Belegdaten
          (Rechnungsnummern, Daten, Beträge, Steuersätze, Positionen), Bankverbindungen, Kartendaten
          in maskierter Form, Kontoumsätze, eingesandte E-Mails samt Anhängen, Notizen, Nutzerkonten
          und Rollen der Mitglieder.
        </li>
        <li>
          Besondere Kategorien personenbezogener Daten (Art. 9 DSGVO) sind nicht Gegenstand der
          Verarbeitung; soweit sie dennoch in Belegen stehen (z. B. Arztrechnungen), verarbeitet der
          Anbieter sie nur als Teil des Belegs.
        </li>
      </ul>

      <h2 className={H2}>5. Weisungen</h2>
      <p>
        Der Anbieter verarbeitet die Daten nur auf dokumentierte Weisung des Kunden, auch bei
        Übermittlungen in Drittländer, sofern er nicht gesetzlich zu einer Verarbeitung verpflichtet
        ist; dann teilt er dem Kunden diese Pflicht vorher mit, soweit das Gesetz es erlaubt. Die
        AGB, dieser Vertrag und die Nutzung der Funktionen der App sind die Weisungen des Kunden.
        Hält der Anbieter eine Weisung für rechtswidrig, informiert er den Kunden unverzüglich.
      </p>

      <h2 className={H2}>6. Pflichten des Anbieters</h2>
      <ul className="list-disc space-y-1 pl-5">
        <li>
          Alle Personen, die Zugang zu den Daten haben, sind zur Vertraulichkeit verpflichtet
          (Datengeheimnis nach § 6 DSG), auch über das Ende ihrer Tätigkeit hinaus.
        </li>
        <li>
          Der Anbieter trifft die technischen und organisatorischen Maßnahmen nach Art. 32 DSGVO
          (Anlage 1) und passt sie dem Stand der Technik an, ohne das Schutzniveau zu senken.
        </li>
        <li>
          Der Anbieter verarbeitet die Daten nur in der EU, soweit nicht Anlage 2 eine Übermittlung
          mit geeigneten Garantien vorsieht.
        </li>
        <li>
          Der Anbieter nutzt die Daten nicht für eigene Zwecke, insbesondere nicht zum Training von
          KI-Modellen.
        </li>
      </ul>

      <h2 className={H2}>7. Unterauftragsverarbeiter</h2>
      <p>
        Der Kunde genehmigt die in Anlage 2 genannten Unterauftragsverarbeiter. Der Anbieter
        verpflichtet sie vertraglich zu denselben Datenschutzpflichten. Über die Hinzuziehung oder
        den Austausch eines Unterauftragsverarbeiters informiert er den Kunden mindestens 30 Tage
        vorher per E-Mail. Der Kunde kann innerhalb dieser Frist aus wichtigem
        datenschutzrechtlichem Grund widersprechen; kann der Anbieter den Widerspruch nicht
        ausräumen, darf der Kunde den Vertrag zum Wirksamwerden der Änderung kündigen.
      </p>

      <h2 className={H2}>8. Unterstützung des Kunden</h2>
      <ul className="list-disc space-y-1 pl-5">
        <li>
          Der Anbieter unterstützt den Kunden mit geeigneten Maßnahmen bei der Beantwortung von
          Anträgen Betroffener (Art. 15 bis 22 DSGVO). Auskunft, Berichtigung und Export kann der
          Kunde weitgehend selbst in der App vornehmen. Wenden sich Betroffene direkt an den
          Anbieter, leitet er die Anfrage unverzüglich an den Kunden weiter.
        </li>
        <li>
          Der Anbieter meldet dem Kunden eine Verletzung des Schutzes personenbezogener Daten
          unverzüglich, spätestens innerhalb von 48 Stunden nach Kenntnis, mit den Angaben nach Art.
          33 Abs. 3 DSGVO, soweit sie vorliegen.
        </li>
        <li>
          Er unterstützt den Kunden bei Datenschutz-Folgenabschätzungen und vorherigen
          Konsultationen der Aufsichtsbehörde (Art. 35 und 36 DSGVO).
        </li>
      </ul>

      <h2 className={H2}>9. Löschung und Rückgabe</h2>
      <p>
        Der Kunde kann seine Daten jederzeit in strukturierten, maschinenlesbaren Formaten (JSON,
        CSV, PDF und die Originaldateien) exportieren. Nach Ende des Nutzungsvertrags bleiben die
        Daten für den in den AGB genannten Abrufzeitraum zur Rückgabe abrufbar; danach löscht der
        Anbieter alle Daten des Kunden, soweit keine gesetzliche Pflicht zur Aufbewahrung besteht.
        Sicherungskopien werden innerhalb von 30 Tagen nach der Löschung überschrieben. Auf Anfrage
        bestätigt der Anbieter die Löschung.
      </p>

      <h2 className={H2}>10. Nachweise und Kontrollen</h2>
      <p>
        Der Anbieter stellt dem Kunden alle Informationen zum Nachweis seiner Pflichten bereit und
        ermöglicht Überprüfungen einschließlich Inspektionen durch den Kunden oder einen von ihm
        beauftragten, zur Verschwiegenheit verpflichteten Prüfer, nach angemessener Vorankündigung
        und während üblicher Geschäftszeiten. Aktuelle Prüfberichte und Zertifizierungen der
        Unterauftragsverarbeiter können als Nachweis dienen.
      </p>

      <h2 className={H2}>11. Schlussbestimmungen</h2>
      <p>
        Bei Widersprüchen geht dieser Vertrag den AGB vor, soweit es um den Schutz personenbezogener
        Daten geht. Im Übrigen gelten die Schlussbestimmungen der AGB. Datenschutzanfragen: <Mail />
      </p>

      <h2 className={H2}>Anlage 1: Technische und organisatorische Maßnahmen</h2>
      <ul className="list-disc space-y-1 pl-5">
        <li>Verschlüsselung bei der Übertragung: alle Verbindungen ausschließlich über TLS.</li>
        <li>
          Speicherort: Datenbank und Belege liegen auf Servern des Hosting-Anbieters im nach ISO/IEC
          27001 zertifizierten Rechenzentrum Falkenstein (Deutschland) mit physischer
          Zutrittskontrolle.
        </li>
        <li>
          Zugriffskontrolle: rollenbasierte Berechtigungen (Inhaber, Mitglied, Steuerberatung, Nur
          Lesen) und Trennung der Betriebe, serverseitig durch Zugriffsregeln der Datenbank
          durchgesetzt; Daten eines anderen Betriebs sind nicht abrufbar. Belegdateien sind nur mit
          einem kurzlebigen Zugriffsschlüssel abrufbar.
        </li>
        <li>Passwörter werden nur als Hash gespeichert.</li>
        <li>
          Administrativer Zugang zu Produktivsystemen nur mit persönlichem Schlüssel; Anmeldung mit
          Passwort ist auf den Servern abgeschaltet.
        </li>
        <li>
          Datensicherung: tägliche Sicherungen mit Kopien an einem zweiten Standort innerhalb der
          EU; die Wiederherstellung wird regelmäßig geprüft.
        </li>
        <li>
          Unveränderbarkeit: verbuchte Belege können nicht gelöscht werden, das Journal wächst nur
          und verkettet jede Buchung mit der vorherigen.
        </li>
        <li>
          Minimaler Personalzugriff: Zugriff auf Kundendaten nur durch wenige berechtigte Personen,
          nur soweit für Betrieb oder Support nötig.
        </li>
        <li>
          Löschkonzept: Löschung auf Anweisung des Kunden und nach dem Abrufzeitraum, Überschreiben
          der Sicherungen innerhalb von 30 Tagen.
        </li>
        <li>Automatisierte Tests jeder Änderung vor der Auslieferung.</li>
      </ul>

      <h2 className={H2}>Anlage 2: Unterauftragsverarbeiter</h2>
      <Subprocessors />
      <p>
        Übermittlungen an Konzerngesellschaften in den USA stützen sich auf das EU-US Data Privacy
        Framework und EU-Standardvertragsklauseln. Plausible erhält keine Inhalte der Belege und
        keine Daten über einzelne Personen; Sentry erhält nur technische Fehlerberichte ohne
        Formular- und Anfrageinhalte, gespeichert in der EU.
      </p>
    </>
  );
}
